import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Container, Row, Col, Button, Form } from 'react-bootstrap';
import { FaBold, FaItalic, FaListUl } from 'react-icons/fa';
import Notes from "../../../../Pages/Images/WhatsApp Image 2025-06-12 at 5.33.20 PM.webp";
import Navbar from "../../../../Pages/Navbar/JobSeekerNavbar";
import JobSeekerNavbar from '../../../../Pages/Navbar/JobSeekerNavbar';
import './AboutDetails.css';
import Swal from "sweetalert2";
import BaseUrl from "../../../../ApiServices/BaseUrl";

const AboutDetails = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { userId } = location.state || {};
  
  // Log userId to console
  useEffect(() => {
    console.log('Received userId:', userId);
  }, [userId]);
  
  // State for form fields
  const [description, setDescription] = useState("");
  const [wordCount, setWordCount] = useState(0);
  const [isSaving, setIsSaving] = useState(false);

  // Update word count when description changes
  useEffect(() => {
    const words = description.trim() ? description.trim().split(/\s+/) : [];
    setWordCount(words.length);
  }, [description]);

  // Show success alert using SweetAlert2
  const showSuccessAlert = () => {
    Swal.fire({
      title: 'Success!',
      text: 'About details saved successfully!',
      icon: 'success',
      confirmButtonText: 'OK',
      confirmButtonColor: '#3085d6',
    });
  };

  // Handle save data to API
  const handleSave = async () => {
    if (!userId) {
      alert('User ID is missing. Please try again.');
      return false;
    }

    if (wordCount < 50) {
      Swal.fire({
        title: 'Warning!',
        text: 'Please enter at least 50 words for your resume description.',
        icon: 'warning',
        confirmButtonText: 'OK',
        confirmButtonColor: '#3085d6',
      });
      return false;
    }

    setIsSaving(true);
    
    try {
      // Prepare the data object for API
      const formData = {
        user_id: userId,
        dtr_user_id: userId,
        dtr_resume_description: description
      };

      console.log('Saving about details:', formData);

      const response = await fetch(`${BaseUrl}/api/job-seeker/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        const result = await response.json();
        console.log('API Response:', result);
        showSuccessAlert();
        return true;
      } else {
        const errorData = await response.json();
        console.error('Server error:', errorData);
        throw new Error(errorData.message || 'Failed to save about details');
      }
    } catch (error) {
      console.error('Error saving about details:', error);
      Swal.fire({
        title: 'Error!',
        text: error.message || 'Error saving about details. Please try again.',
        icon: 'error',
        confirmButtonText: 'OK',
        confirmButtonColor: '#d33',
      });
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  // Handle next step
  const handleNext = async () => {
    // Save data first, then navigate
    const success = await handleSave();
    if (success) {
      navigate('/next-page', { 
        state: { userId: userId } 
      });
    }
  };

  // Handle back step
  const handleBack = () => {
    navigate('/qualifications', { 
      state: { userId: userId } 
    });
  };

  // Handle navigation button clicks
  const handleNavigation = (buttonName) => {
    if (buttonName === "Next") {
      handleNext();
    } else if (buttonName === "Back") {
      handleBack();
    } else if (buttonName === "Save") {
      handleSave();
    }
  };

  // Format text with simple formatting options
  const formatText = (format) => {
    let formattedText = description;
    
    switch(format) {
      case 'bold':
        formattedText = description + " **bold text** ";
        break;
      case 'italic':
        formattedText = description + " *italic text* ";
        break;
      case 'list':
        formattedText = description + "\n- List item 1\n- List item 2\n- List item 3";
        break;
      default:
        break;
    }
    
    setDescription(formattedText);
  };

  return (
    <>
      <JobSeekerNavbar/>
      <div style={{marginTop:"70px"}}>
        <Navbar />
        <Container className="about-details-container my-4" style={{ marginTop: "84px" }}>
          {/* Fixed Header */}
          <div className="about-details-header d-flex align-items-center justify-content-center">
            <div className="about-details-icon me-2">
              <img
                src={Notes}
                alt="notes-icon"
                className="about-details-notes"
              />
            </div>
            <h1 className="about-details-title mb-0">Provide details about you</h1>
          </div>

          <h5 className="about-details-label mb-2 mt-3">
            Resume Description (Explain your work experience and personality)
            <span className="text-danger">*</span>
          </h5>

          <div className="about-details-editor-container p-3 mx-auto">
            <div className="about-details-toolbar mb-2 d-flex gap-2">
              <Button 
                variant="light" 
                className="about-details-editor-btn"
                onClick={() => formatText('bold')}
              >
                <FaBold />
              </Button>
              <Button 
                variant="light" 
                className="about-details-editor-btn"
                onClick={() => formatText('italic')}
              >
                <FaItalic />
              </Button>
              <Button 
                variant="light" 
                className="about-details-editor-btn"
                onClick={() => formatText('list')}
              >
                <FaListUl />
              </Button>
            </div>

            <Form.Control
              as="textarea"
              rows={10}
              className="about-details-textarea p-3"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Enter your resume description here (minimum 50 words)..."
            />
            
            <div className={`about-details-word-count mt-2 text-end ${wordCount < 50 ? 'text-danger' : 'text-success'}`}>
              Word count: {wordCount} / 50
            </div>

          </div>

          {/* Navigation Buttons */}
          <div className="about-details-navigation mb-4 mt-4">
            <Row className="g-3">
              <Col xs={12} sm={4}>
                <Button 
                  className="about-details-nav-btn w-100"
                  onClick={() => handleNavigation("Back")}
                >
                  Back
                </Button>
              </Col>
              <Col xs={12} sm={4}>
                <Button 
                  className="about-details-nav-btn w-100"
                  onClick={() => handleNavigation("Save")}
                  disabled={isSaving}
                >
                  {isSaving ? 'Saving...' : 'Save'}
                </Button>
              </Col>
              <Col xs={12} sm={4}>
                <Button 
                  className="about-details-nav-btn w-100"
                  onClick={() => handleNavigation("Next")}
                  disabled={isSaving || wordCount < 50}
                >
                  {isSaving ? 'Saving...' : 'Next'}
                </Button>
              </Col>
            </Row>
          </div>
        </Container>
      </div>
    </>
  );
};

export default AboutDetails;